import type { FormEvent, ReactElement, ReactNode } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { policyDisallowsEdgeWhitespace } from "@/features/first-credential/first-credential-client";
import {
  PASSWORD_EDGE_WHITESPACE_MESSAGE,
  PasswordRequirements,
  describePasswordPolicy,
} from "@/features/first-credential/password-requirements";
import { secretFieldRef } from "@/features/first-credential/secret-field-ref";
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Checkbox,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui";
import { reportUnknownEditableField } from "@/shared/telemetry/safe-telemetry";
import type {
  DeliveryPreference,
  InvitationLookupReady,
  MemberLookupReady,
  ReferenceOption,
  ValidationFailed,
} from "./registration-invitation-client";
import {
  DELIVERY_PREFERENCE_OPTIONS,
  NO_SELECTION_VALUE,
  createVariantInitialValues,
  validateVariantValues,
  type VariantFieldDefinition,
  type VariantFieldErrors,
  type VariantFormValues,
  type VariantRenderPlan,
} from "./variant-fields";

/**
 * Variant-driven registration form (CLMS-113 T068, US3). Renders exactly the
 * compiled-allowlist ∩ server-editableFields intersection from the supplied
 * render plan; unknown server field names are ignored and reported through
 * safe telemetry (field NAME only). No identity, role, status, ID, or
 * payment/provider field ever renders or submits — none is compiled.
 * Reference-backed fields render only CLOSED controls over the server's
 * variant option catalogs — shared Select for single-value references and
 * checkbox groups for multi-value references — with no free-entry fallback.
 * A missing/empty required catalog never reaches this form: the page fails
 * closed into the generic unavailable state before rendering it.
 */

const SECTION_ORDER = [
  "profile",
  "business-address",
  "mailing-address",
  "details",
  "publications",
] as const;

const SECTION_LEGENDS: Record<(typeof SECTION_ORDER)[number], string | null> = {
  profile: null,
  "business-address": "Business address",
  "mailing-address": "Mailing address",
  details: "Additional details",
  publications: "Publication preferences",
};

export function fieldInputId(path: string): string {
  return `registration-${path.replace(/\./g, "-")}`;
}

type MemberPasswordPolicy = MemberLookupReady["passwordPolicy"];

const PASSWORD_INPUT_ID = fieldInputId("password");
const PASSWORD_HELP_ID = `${PASSWORD_INPUT_ID}-help`;
const PASSWORD_ON_FILE_ID = `${PASSWORD_INPUT_ID}-on-file`;
const PASSWORD_ERROR_ID = `${PASSWORD_INPUT_ID}-error`;
const PASSWORD_CONFIRM_INPUT_ID = `${PASSWORD_INPUT_ID}-confirm`;
const PASSWORD_CONFIRM_ERROR_ID = `${PASSWORD_CONFIRM_INPUT_ID}-error`;
const PASSWORD_MISMATCH_MESSAGE = "Both password fields must match.";
const PASSWORD_ON_FILE_MESSAGE =
  "Your password was already submitted with this registration. You only need to enter it again if we ask you to.";
/** Announced when the on-file lock lifts (business edit / resubmit_required)
 * so the transition is not silent for assistive technology (WCAG 4.1.3). */
const PASSWORD_REOPENED_MESSAGE = "Enter your password again to complete your registration.";

// Server mirror (clm-web-service-auth validateMemberPassword): PasswordValue
// maxLength 128 counted in UTF-16 units, ASCII character classes, and this
// exact symbol set. Anything the server would 422 must fail here first, or
// the fields are cleared on dispatch with misleading guidance.
const PASSWORD_MAX_LENGTH = 128;
const PASSWORD_SYMBOL_PATTERN = /[`~!@#$%^&*()_\-+={[\]}\\|:;"'<,>.?/]/u;

/** Client-side mirror of the server policy (shared 011-21 helpers). Never
 * trims or normalises: the value is validated exactly as typed (a passing
 * password is sent verbatim). */
function validatePassword(policy: MemberPasswordPolicy, password: string): string | null {
  if (policyDisallowsEdgeWhitespace(policy) && /^\s|\s$/u.test(password)) {
    return PASSWORD_EDGE_WHITESPACE_MESSAGE;
  }
  const meetsPolicy =
    password.length >= policy.minimumLength &&
    password.length <= PASSWORD_MAX_LENGTH &&
    (!policy.requireUppercase || /[A-Z]/u.test(password)) &&
    (!policy.requireLowercase || /[a-z]/u.test(password)) &&
    (!policy.requireNumber || /[0-9]/u.test(password)) &&
    (!policy.requireSymbol || PASSWORD_SYMBOL_PATTERN.test(password));
  return meetsPolicy ? null : describePasswordPolicy(policy);
}

export interface RegistrationFormProps {
  /** Compiled ∩ server field intersection for the resolved variant. */
  plan: VariantRenderPlan;
  /** Allow-listed prefill from lookup; never authoritative on submit. */
  prefill: InvitationLookupReady["prefill"];
  expiresAt: string;
  submitting: boolean;
  /** Completion Retry-After hold (US4): disables submission for the
   * advertised rate-limit window while keeping the form values intact. */
  submitHeld?: boolean;
  /** FR-050 (011-15 UI contract L155): the server answered the member
   * `finalize` with `resubmit_required`. The form returns to Editing with
   * BOTH credential fields empty — flipping this true clears them even if
   * anything reached the DOM controls in the meantime. */
  resubmitRequired?: boolean;
  /** Recognized field errors mapped from a 422 ValidationFailed outcome. */
  serverFieldErrors?: ValidationFailed["fieldErrors"];
  /** Member credential step (011-15 FR-050): when the lookup advertises a
   * password policy the "Create your password" group renders after the
   * profile sections and before the submit action. Absent for Direct
   * Fellows — no credential control ever renders for them. */
  passwordPolicy?: MemberLookupReady["passwordPolicy"];
  /** FR-050 (011-15 UI contract L153/L154/L158): the current form's
   * `initial` already carried the password over the wire and the SPA does
   * not retain it, so any re-submit of this unchanged form is a `finalize`
   * that needs no credential. While true the credential group renders
   * locked, empty and optional with an explanatory note — nothing a member
   * types can be silently discarded — and submission skips credential
   * validation. Flips back when a business field diverges (new key, L156)
   * or the server answers `resubmit_required` (L155). */
  credentialsOnFile?: boolean;
  /** Fired on every business-field change with the next values and the
   * derived cleared paths so the page can compare the live form against
   * the last failed submission's fingerprint (L156/L209). */
  onValuesChange?(values: VariantFormValues, clearedPaths: ReadonlySet<string>): void;
  /** `clearedPaths` carries the rendered nullable paths the user explicitly
   * cleared after they held a value (prefilled or user-entered) — the
   * request builder submits null for those (tri-state contract); untouched
   * or never-populated fields stay omitted (no-change). `password` is the
   * raw credential value read once at submit (empty when no policy renders);
   * both password controls are cleared as soon as it is handed over. */
  onSubmit(values: VariantFormValues, clearedPaths: ReadonlySet<string>, password: string): void;
}

export function RegistrationForm({
  plan,
  prefill,
  expiresAt,
  submitting,
  submitHeld = false,
  resubmitRequired = false,
  serverFieldErrors,
  passwordPolicy,
  credentialsOnFile = false,
  onValuesChange,
  onSubmit,
}: RegistrationFormProps): ReactElement {
  const [values, setValues] = useState<VariantFormValues>(() =>
    createVariantInitialValues(plan, prefill),
  );
  const [fieldErrors, setFieldErrors] = useState<VariantFieldErrors>({});
  // Credential controls are UNCONTROLLED: the password never enters React
  // state (double-buffered fibers would retain it after a clear). Values are
  // read from the DOM once at submit and the inputs are emptied immediately
  // after the request is handed over. Only error TEXT is state.
  const passwordInputRef = useRef<HTMLInputElement | null>(null);
  const confirmationInputRef = useRef<HTMLInputElement | null>(null);
  // Detach-time clearing: an unmount before dispatch (route change, page
  // teardown) wipes the node value in place so a retained DOM node cannot
  // keep an undispatched secret (same pattern as /set-password).
  const [passwordFieldRef] = useState(() => secretFieldRef(passwordInputRef));
  const [confirmationFieldRef] = useState(() => secretFieldRef(confirmationInputRef));
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [confirmationError, setConfirmationError] = useState<string | null>(null);
  // Live-region text for the credential group's lock/re-open transitions.
  // Starts empty so the first render announces nothing; set to the on-file
  // note when the group locks and to the re-entry prompt when it re-opens.
  const [credentialStatus, setCredentialStatus] = useState<string | null>(() =>
    credentialsOnFile ? PASSWORD_ON_FILE_MESSAGE : null,
  );
  const formRef = useRef<HTMLFormElement | null>(null);
  // Pristine-vs-touched clear tracking: paths that ever held a non-empty
  // value (from prefill or user entry). A now-empty path in this set was
  // explicitly cleared by the user and submits null; a never-populated
  // path stays omitted from the completion request (no-change). Seeded
  // once from the pristine initial values (first-render `values`).
  const everPopulatedRef = useRef<Set<string> | null>(null);
  everPopulatedRef.current ??= new Set(
    Object.entries(values)
      .filter(([, value]) => typeof value === "string" && value.trim() !== "")
      .map(([path]) => path),
  );
  const everPopulated = everPopulatedRef.current;

  // Contract: unknown server field names are reported through safe telemetry
  // — field NAME only, never any value or prefill data.
  useEffect(() => {
    for (const field of plan.unknownServerFields) {
      reportUnknownEditableField(field);
    }
  }, [plan.unknownServerFields]);

  const renderedPaths = useMemo(
    () => new Set(plan.fields.map((definition) => definition.path)),
    [plan.fields],
  );

  const mergedErrors = useMemo(() => {
    const merged: VariantFieldErrors = { ...fieldErrors };
    for (const serverError of serverFieldErrors ?? []) {
      // Canonical paths keep established profile leaves flat; tolerate a
      // structural profile. prefix defensively.
      const field = serverError.field.replace(/^profile\./, "");
      if (renderedPaths.has(field) && !merged[field]) {
        merged[field] = serverError.message;
      }
    }
    return merged;
  }, [fieldErrors, serverFieldErrors, renderedPaths]);

  const sections = useMemo(() => {
    const grouped = new Map<(typeof SECTION_ORDER)[number], VariantFieldDefinition[]>();
    for (const definition of plan.fields) {
      const section = grouped.get(definition.section) ?? [];
      section.push(definition);
      grouped.set(definition.section, section);
    }
    return SECTION_ORDER.filter((section) => grouped.has(section)).map((section) => ({
      section,
      legend: SECTION_LEGENDS[section],
      fields: grouped.get(section) ?? [],
    }));
  }, [plan.fields]);

  const expiryDate = useMemo(() => formatExpiry(expiresAt), [expiresAt]);

  // Server `password_policy` 422 (011-15): restate the policy inline rather
  // than echo the server text; the control is marked invalid and described
  // by the error. Cleared by the next submission (mutation reset) or by a
  // newer client-side error.
  const serverPasswordError =
    passwordPolicy !== undefined &&
    (serverFieldErrors ?? []).some((fieldError) => fieldError.field === "password")
      ? describePasswordPolicy(passwordPolicy)
      : null;
  const effectivePasswordError = passwordError ?? serverPasswordError;

  const clearPasswordControls = useCallback((): void => {
    if (passwordInputRef.current) {
      passwordInputRef.current.value = "";
    }
    if (confirmationInputRef.current) {
      confirmationInputRef.current.value = "";
    }
  }, []);

  // FR-050 request discipline: while a completion request (initial, timed or
  // manual finalize) is in flight, EVERY control is locked — a mid-flight
  // profile edit would silently desynchronize the canonical form from the
  // idempotency key that is already on the wire (011-15 L213). A Retry-After
  // hold has nothing in flight, so it only withholds the submit control.
  const locked = submitting;
  const submitBlocked = submitting || submitHeld;
  // The credential group has one extra lock reason: this key's password is
  // already on file (L154), so the group takes no input until a business
  // edit or `resubmit_required` re-opens it.
  const credentialLocked = locked || credentialsOnFile;

  // Credentials never survive a dispatch or a resubmit_required re-entry:
  // `submitting` turning true covers the page-driven `finalize` dispatches
  // (Retry-After re-send, support-panel Try again) that bypass handleSubmit;
  // `resubmitRequired` turning true is the L155 return to Editing; the
  // on-file lock drops any stale text before the group is disabled.
  useEffect(() => {
    if (submitting || resubmitRequired || credentialsOnFile) {
      clearPasswordControls();
      setPasswordError(null);
      setConfirmationError(null);
    }
  }, [submitting, resubmitRequired, credentialsOnFile, clearPasswordControls]);

  // Announce the on-file lock and the re-open (WCAG 4.1.3): the container
  // below is always mounted with role=status, so only its TEXT changes. The
  // re-open prompt is announced once per transition, never on first render.
  const credentialsWereOnFileRef = useRef(credentialsOnFile);
  useEffect(() => {
    if (passwordPolicy === undefined) {
      return;
    }
    if (credentialsOnFile) {
      setCredentialStatus(PASSWORD_ON_FILE_MESSAGE);
    } else if (credentialsWereOnFileRef.current) {
      setCredentialStatus(PASSWORD_REOPENED_MESSAGE);
    }
    credentialsWereOnFileRef.current = credentialsOnFile;
  }, [credentialsOnFile, passwordPolicy]);

  const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (submitBlocked) {
      return;
    }

    const errors = validateVariantValues(plan, values);
    setFieldErrors(errors);
    // Credential step: read the uncontrolled fields once, validate the
    // policy mirror (never trimmed) and the client-only confirmation. When
    // the password is already on file for this key the group is locked and
    // empty — the submit is a `finalize` (L154) and nothing is validated.
    let password = "";
    let firstInvalidCredentialId: string | null = null;
    if (passwordPolicy !== undefined && !credentialsOnFile) {
      password = passwordInputRef.current?.value ?? "";
      const confirmation = confirmationInputRef.current?.value ?? "";
      const policyError = validatePassword(passwordPolicy, password);
      const mismatch = policyError === null && password !== confirmation;
      setPasswordError(policyError);
      setConfirmationError(mismatch ? PASSWORD_MISMATCH_MESSAGE : null);
      if (policyError !== null) {
        firstInvalidCredentialId = PASSWORD_INPUT_ID;
      } else if (mismatch) {
        firstInvalidCredentialId = PASSWORD_CONFIRM_INPUT_ID;
      }
    }
    if (Object.keys(errors).length > 0 || firstInvalidCredentialId !== null) {
      // Focus the first invalid field in rendered order (accessibility
      // contract: focus moves to the first error on failed submission).
      const firstInvalid = plan.fields.find((definition) => errors[definition.path]);
      const targetId = firstInvalid ? fieldInputId(firstInvalid.path) : firstInvalidCredentialId;
      if (targetId) {
        formRef.current?.querySelector<HTMLElement>(`#${targetId}`)?.focus();
      }
      return;
    }

    onSubmit(values, computeClearedPaths(plan, values, everPopulated), password);
    // Cleared as soon as the initial request is handed to the network: the
    // raw value never lingers in the DOM across the response.
    clearPasswordControls();
  };

  const setValue = (path: string, value: string | boolean | string[]): void => {
    if (typeof value === "string" && value.trim() !== "") {
      everPopulated.add(path);
    }
    const next: VariantFormValues = { ...values, [path]: value };
    setValues((current) => ({ ...current, [path]: value }));
    onValuesChange?.(next, computeClearedPaths(plan, next, everPopulated));
  };

  return (
    <Card className="shadow-sm">
      <CardHeader>
        <CardTitle className="text-base font-semibold">Your details</CardTitle>
        <CardDescription>
          All fields marked with * are required.
          {expiryDate ? ` This registration link is valid until ${expiryDate}.` : ""}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form ref={formRef} onSubmit={handleSubmit} noValidate className="space-y-6">
          {sections.map(({ section, legend, fields }) => (
            <FormSection key={section} legend={legend}>
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                {fields.map((definition) => (
                  <VariantField
                    key={definition.path}
                    definition={definition}
                    value={values[definition.path]}
                    error={mergedErrors[definition.path]}
                    options={plan.options.get(definition.path)}
                    disabled={locked}
                    onChange={(value) => {
                      setValue(definition.path, value);
                    }}
                  />
                ))}
              </div>
            </FormSection>
          ))}

          {passwordPolicy !== undefined && (
            <FormSection legend="Create your password">
              {/* Always-mounted polite live region (financial-outcome announcer
                  pattern) so the lock and the re-open are both announced; the
                  Password and Confirm controls reference it only while the
                  password is on file. */}
              <p
                id={PASSWORD_ON_FILE_ID}
                aria-live="polite"
                aria-atomic="true"
                className={credentialStatus === null ? "sr-only" : "mb-4 text-sm text-gray-600"}
              >
                {credentialStatus}
              </p>
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div>
                  <Label htmlFor={PASSWORD_INPUT_ID}>
                    {credentialsOnFile ? "Password" : "Password *"}
                  </Label>
                  <Input
                    ref={passwordFieldRef}
                    id={PASSWORD_INPUT_ID}
                    name="password"
                    type="password"
                    autoComplete="new-password"
                    disabled={credentialLocked}
                    aria-required={!credentialsOnFile}
                    aria-invalid={effectivePasswordError !== null}
                    aria-describedby={
                      credentialsOnFile
                        ? `${PASSWORD_ON_FILE_ID} ${PASSWORD_HELP_ID}`
                        : effectivePasswordError !== null
                          ? `${PASSWORD_HELP_ID} ${PASSWORD_ERROR_ID}`
                          : PASSWORD_HELP_ID
                    }
                  />
                  <PasswordRequirements
                    policy={passwordPolicy}
                    id={PASSWORD_HELP_ID}
                    className="mt-2 text-sm text-gray-500"
                  />
                  {effectivePasswordError !== null && (
                    <p id={PASSWORD_ERROR_ID} className="mt-1 text-sm text-alert-red">
                      {effectivePasswordError}
                    </p>
                  )}
                </div>
                <div>
                  <Label htmlFor={PASSWORD_CONFIRM_INPUT_ID}>
                    {credentialsOnFile ? "Confirm password" : "Confirm password *"}
                  </Label>
                  {/* Client-only confirmation: no name, so it is never part
                      of any submittable form data. */}
                  <Input
                    ref={confirmationFieldRef}
                    id={PASSWORD_CONFIRM_INPUT_ID}
                    type="password"
                    autoComplete="new-password"
                    disabled={credentialLocked}
                    aria-required={!credentialsOnFile}
                    aria-invalid={confirmationError !== null}
                    aria-describedby={
                      credentialsOnFile
                        ? PASSWORD_ON_FILE_ID
                        : confirmationError !== null
                          ? PASSWORD_CONFIRM_ERROR_ID
                          : undefined
                    }
                  />
                  {confirmationError !== null && (
                    <p id={PASSWORD_CONFIRM_ERROR_ID} className="mt-1 text-sm text-alert-red">
                      {confirmationError}
                    </p>
                  )}
                </div>
              </div>
            </FormSection>
          )}

          {/* h-auto whitespace-normal: CardContent p-6 leaves ~238px at a
              320px viewport — marginal for the pending label, so allow wrap. */}
          <Button
            type="submit"
            className="h-auto min-h-12 w-full whitespace-normal"
            disabled={submitBlocked}
          >
            {submitting ? "Completing registration…" : "Complete registration"}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

/** Rendered single-value paths that are empty now but ever held a value —
 * the user's explicit clears. Only text and single-select controls
 * participate: multi-selects clear via empty arrays and delivery selects
 * always carry a value. Required fields cannot reach submit empty. */
function computeClearedPaths(
  plan: VariantRenderPlan,
  values: VariantFormValues,
  everPopulated: ReadonlySet<string>,
): ReadonlySet<string> {
  const cleared = new Set<string>();
  for (const definition of plan.fields) {
    const kind = definition.control.kind;
    if (kind !== "text" && kind !== "reference-select") {
      continue;
    }
    const path = definition.path;
    if (everPopulated.has(path) && String(values[path] ?? "").trim() === "") {
      cleared.add(path);
    }
  }
  return cleared;
}

function FormSection({
  legend,
  children,
}: {
  legend: string | null;
  children: ReactNode;
}): ReactElement {
  if (!legend) {
    return <div>{children}</div>;
  }
  return (
    <fieldset className="space-y-4 border-t border-border pt-2">
      <legend className="pt-4 text-base font-semibold">{legend}</legend>
      {children}
    </fieldset>
  );
}

function VariantField({
  definition,
  value,
  error,
  options,
  disabled,
  onChange,
}: {
  definition: VariantFieldDefinition;
  value: string | boolean | string[] | undefined;
  error?: string;
  options?: readonly ReferenceOption[];
  disabled: boolean;
  onChange(value: string | boolean | string[]): void;
}): ReactElement {
  const inputId = fieldInputId(definition.path);
  const errorId = `${inputId}-error`;
  const label = definition.required ? `${definition.label} *` : definition.label;
  const control = definition.control;

  const errorText = error ? (
    <p id={errorId} className="mt-1 text-sm text-alert-red">
      {error}
    </p>
  ) : null;

  switch (control.kind) {
    case "text":
      return (
        <div>
          <Label htmlFor={inputId}>{label}</Label>
          <Input
            id={inputId}
            type={control.type}
            value={String(value ?? "")}
            disabled={disabled}
            onChange={(event) => {
              onChange(event.target.value);
            }}
            aria-invalid={Boolean(error)}
            aria-describedby={error ? errorId : undefined}
            aria-required={definition.required || undefined}
            autoComplete={control.autoComplete}
            maxLength={control.maxLength}
            placeholder={control.placeholder}
          />
          {errorText}
        </div>
      );
    case "delivery-select":
      return (
        <div>
          <Label htmlFor={inputId}>{label}</Label>
          <Select
            value={String(value ?? "none")}
            disabled={disabled}
            onValueChange={(next: DeliveryPreference) => {
              onChange(next);
            }}
          >
            <SelectTrigger
              id={inputId}
              aria-invalid={Boolean(error)}
              aria-describedby={error ? errorId : undefined}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {DELIVERY_PREFERENCE_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errorText}
        </div>
      );
    case "checkbox":
      return (
        <div className="md:col-span-2">
          <div className="flex items-center gap-2">
            <Checkbox
              id={inputId}
              checked={value === true}
              disabled={disabled}
              onCheckedChange={(checked) => {
                onChange(checked === true);
              }}
              aria-invalid={Boolean(error)}
              aria-describedby={error ? errorId : undefined}
            />
            <Label htmlFor={inputId} className="cursor-pointer">
              {label}
            </Label>
          </div>
          {errorText}
        </div>
      );
    case "reference-select": {
      const selected = String(value ?? "");
      // Optional closed selects stay clearable: a leading clear item maps
      // back to the empty value so a picked value can be undone (legacy
      // "--Please Select--" parity). 011-15 F5 (DECIDED 2026-09-04): the
      // clear item reuses the field's placeholder text so the empty state
      // reads as ONE consistent string (trigger and menu) matching the
      // placeholder intent — never a diverging "No selection". The select
      // stays controlled by resolving the empty value to the sentinel item.
      // A required select (FR-028 server-declared, e.g. the member program
      // for an uncovered company) has no clear item; its empty value stays
      // controlled as "" so the placeholder shows until a catalog value is
      // chosen and client validation blocks completion meanwhile.
      const clearable = !definition.required;
      return (
        <div>
          <Label htmlFor={inputId}>{label}</Label>
          <Select
            value={selected || (clearable ? NO_SELECTION_VALUE : "")}
            disabled={disabled}
            onValueChange={(next: string) => {
              onChange(next === NO_SELECTION_VALUE ? "" : next);
            }}
          >
            <SelectTrigger
              id={inputId}
              aria-required={definition.required || undefined}
              aria-invalid={Boolean(error)}
              aria-describedby={error ? errorId : undefined}
            >
              <SelectValue placeholder={control.placeholder} />
            </SelectTrigger>
            <SelectContent>
              {clearable && (
                <SelectItem value={NO_SELECTION_VALUE}>{control.placeholder}</SelectItem>
              )}
              {(options ?? []).map((option) => (
                <SelectItem key={option.reference} value={option.reference}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errorText}
        </div>
      );
    }
    case "reference-multi": {
      const selected = new Set(Array.isArray(value) ? value : []);
      const toggle = (reference: string, checked: boolean): void => {
        const next = new Set(selected);
        if (checked) {
          next.add(reference);
        } else {
          next.delete(reference);
        }
        onChange([...next]);
      };
      return (
        // aria-invalid is not conforming on role=group, so a group-level
        // error marks the individual checkboxes instead (matching the
        // single-checkbox control) while the fieldset keeps the
        // aria-describedby wiring to the error text.
        <fieldset
          id={inputId}
          tabIndex={-1}
          className="md:col-span-2"
          aria-describedby={error ? errorId : undefined}
        >
          <legend className="text-sm font-medium leading-none">{label}</legend>
          <ul className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
            {(options ?? []).map((option) => {
              const optionId = `${inputId}-${option.reference.replace(/[^a-zA-Z0-9_-]/g, "_")}`;
              return (
                <li key={option.reference} className="flex items-center gap-2">
                  <Checkbox
                    id={optionId}
                    checked={selected.has(option.reference)}
                    disabled={disabled}
                    onCheckedChange={(checked) => {
                      toggle(option.reference, checked === true);
                    }}
                    aria-invalid={error ? true : undefined}
                  />
                  <Label htmlFor={optionId} className="cursor-pointer font-normal">
                    {option.label}
                  </Label>
                </li>
              );
            })}
          </ul>
          {errorText}
        </fieldset>
      );
    }
  }
}

function formatExpiry(expiresAt: string): string | null {
  const parsed = new Date(expiresAt);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }
  // User locale, date AND time from the instant: a date-only render of a UTC
  // instant overstates validity (e.g. an expiry at 00:00Z reads as the whole
  // local day). 011-15 F6 (DECIDED 2026-09-04): the timezone abbreviation is
  // explicit — viewer-local time without it reads as a different instant
  // than the UTC time stated in the invitation email.
  return new Intl.DateTimeFormat(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
  }).format(parsed);
}
